"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Shield, Globe, Lock, Zap, Server, Eye, EyeOff, Wifi, Star, CheckCircle, Info, AlertCircle, MessageSquare } from "lucide-react";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  Shield, shield: Shield,
  Globe, globe: Globe,
  Lock, lock: Lock,
  Zap, zap: Zap,
  Server, server: Server,
  Eye, eye: Eye,
  EyeOff, "eye-off": EyeOff,
  Wifi, wifi: Wifi,
  Star, star: Star,
  CheckCircle, "check-circle": CheckCircle,
  Info, info: Info,
  AlertCircle, "alert-circle": AlertCircle,
  MessageSquare, "message-square": MessageSquare,
};

interface RichTextProps {
  badge?: {
    icon?: string;
    text?: string;
  } | null;
  content: string;
  alignment?: "left" | "center" | "right";
}

const richTextComponents = {
  h1: ({ node, ...props }: any) => (
    <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-foreground via-foreground to-foreground/70 bg-clip-text text-transparent mb-8 mt-12 first:mt-0 tracking-tight" {...props} />
  ),
  h2: ({ node, ...props }: any) => (
    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 mt-10 tracking-tight" {...props} />
  ),
  h3: ({ node, ...props }: any) => (
    <h3 className="text-2xl md:text-3xl font-semibold text-foreground mb-4 mt-8" {...props} />
  ),
  h4: ({ node, ...props }: any) => (
    <h4 className="text-xl font-semibold text-foreground mb-3 mt-6" {...props} />
  ),
  p: ({ node, ...props }: any) => (
    <p className="text-foreground/70 leading-relaxed mb-5 text-base md:text-lg" {...props} />
  ),
  a: ({ node, ...props }: any) => (
    <a className="text-primary font-medium underline underline-offset-4 decoration-primary/30 hover:decoration-primary transition-colors" {...props} />
  ),
  ul: ({ node, ...props }: any) => (
    <ul className="space-y-3 my-6 text-foreground/70" {...props} />
  ),
  ol: ({ node, ...props }: any) => (
    <ol className="space-y-3 my-6 text-foreground/70 list-none counter-reset-item" {...props} />
  ),
  li: ({ node, children, ...props }: any) => {
    // Check if parent is ol or ul by looking at the first character
    const isOrdered = props.node?.position?.start?.offset !== undefined;
    return (
      <li className="leading-relaxed text-base md:text-lg pl-8 relative before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-gradient-to-br before:from-primary before:to-secondary" {...props}>
        {children}
      </li>
    );
  },
  blockquote: ({ node, children, ...props }: any) => (
    <blockquote className="my-10 py-6 px-8 md:px-10 bg-muted/50 rounded-2xl border-l-4 border-blue-500" {...props}>
      <div className="text-xl md:text-2xl text-foreground leading-relaxed font-medium">
        {children}
      </div>
    </blockquote>
  ),
  strong: ({ node, ...props }: any) => (
    <strong className="font-bold text-foreground" {...props} />
  ),
  em: ({ node, ...props }: any) => (
    <em className="italic text-foreground/80" {...props} />
  ),
  hr: ({ node, ...props }: any) => (
    <hr className="my-12 border-none h-px bg-gradient-to-r from-transparent via-foreground/20 to-transparent" {...props} />
  ),
  code: ({ node, inline, ...props }: any) =>
    inline ? (
      <code className="bg-gradient-to-r from-primary/10 to-secondary/10 px-2 py-1 rounded-lg text-sm font-mono text-foreground/90 border border-primary/10" {...props} />
    ) : (
      <code className="text-sm font-mono" {...props} />
    ),
  pre: ({ node, ...props }: any) => (
    <pre className="bg-foreground text-white rounded-2xl p-6 my-8 overflow-x-auto text-sm shadow-xl" {...props} />
  ),
  table: ({ node, ...props }: any) => (
    <div className="overflow-x-auto my-8 rounded-2xl border border-foreground/10 shadow-sm">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: ({ node, ...props }: any) => (
    <thead className="bg-gradient-to-r from-muted to-muted/50" {...props} />
  ),
  th: ({ node, ...props }: any) => (
    <th className="text-left font-semibold text-foreground px-5 py-4 border-b border-foreground/10" {...props} />
  ),
  td: ({ node, ...props }: any) => (
    <td className="px-5 py-4 border-b border-foreground/5 text-foreground/70" {...props} />
  ),
};

const RichText = ({ badge, content, alignment = "left" }: RichTextProps) => {
  const alignmentClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  const BadgeIcon = badge?.icon ? (ICON_MAP[badge.icon] || Shield) : null;

  return (
    <section className="py-24 md:py-32 px-4 md:px-6 bg-gradient-to-b from-white via-white to-muted/30 overflow-hidden">
      <div className={`w-full max-w-3xl mx-auto ${alignmentClasses[alignment]}`}>
        {badge?.text && (
          <BlurFade delay={0.1}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-zinc-100 border border-zinc-200 text-foreground text-[10px] font-bold tracking-widest uppercase mb-8">
              {BadgeIcon && <BadgeIcon size={12} fill="currentColor" />}
              {badge.text}
            </div>
          </BlurFade>
        )}
        <BlurFade delay={badge?.text ? 0.2 : 0.1}>
          <div className="rich-text-content prose-headings:scroll-mt-20 overflow-hidden">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={richTextComponents as any}
            >
              {content || ""}
            </ReactMarkdown>
          </div>
        </BlurFade>
      </div>
    </section>
  );
};

export default RichText;
